import { ref, type Ref } from 'vue'
import type { PhoneCode } from '@/constants/phone-codes'
import { usePhoneNumberFormat } from '../format/usePhoneNumberFormat'
import { findCountryByCode } from '../detect/phone-code-auto-detect.helpers'

/**
 * Повний префікс країни (phoneCode вже містить повний код, напр. 1684 для American Samoa)
 */
function getFullPrefix(country: PhoneCode): string {
    return country.phoneCode
}

/**
 * Composable для управління діалогом вибору коду країни
 * Відповідає за: Single Responsibility - тільки управління діалогом та вибором країни
 * @param getCurrentCountry — поточна вибрана країна (для коректного зняття префіксу при заміні)
 */
export function usePhoneCodeDialog(
    phoneNumber: Ref<string>,
    setCode: (country: PhoneCode) => void,
    onCountryChanged?: () => void,
    getCurrentCountry?: () => PhoneCode | null
) {
    const dialog = ref(false)
    const searchQuery = ref('')
    const { extractNumber } = usePhoneNumberFormat()

    /**
     * Відкриває діалог вибору коду країни
     */
    const openCodesDialog = () => {
        searchQuery.value = ''
        dialog.value = true
    }

    /**
     * Обробляє вибір країни з діалогу.
     * При заміні враховує повний префікс (phoneCode), щоб коректно підставляти новий код.
     */
    const selectCountry = (country: PhoneCode) => {
        setCode(country)
        searchQuery.value = ''

        const digitsOnly = (phoneNumber.value || '').replace(/\D/g, '')
        const currentCountry = (digitsOnly.length >= 2 ? findCountryByCode(digitsOnly) : null)
            ?? getCurrentCountry?.() ?? null
        const prefixLen = currentCountry ? getFullPrefix(currentCountry).length : 0

        let localPart: string
        if (prefixLen > 0 && digitsOnly.length > prefixLen) {
            localPart = digitsOnly.substring(prefixLen)
        } else if (prefixLen > 0 && digitsOnly.length <= prefixLen) {
            localPart = ''
        } else {
            localPart = phoneNumber.value ? extractNumber(phoneNumber.value) : ''
        }

        const newPrefix = getFullPrefix(country)
        phoneNumber.value = localPart ? `+${newPrefix}${localPart}` : `+${newPrefix}`
        onCountryChanged?.()
    }

    return {
        dialog,
        searchQuery,
        openCodesDialog,
        selectCountry,
    }
}

